import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getMe, getAppConfig, logout as apiLogout } from "@/lib/api";

const AuthContext = createContext(null);

// Safe defaults if /api/config is unreachable — keep pricing OFF so we never
// accidentally expose a buy-credits UI before the host has opted in.
const DEFAULT_CONFIG = {
  show_pricing: false,
  free_credits_default: 10,
  app_name: "OfferAlchemy",
  app_logo_url: "https://therahulgangurde.com/assets/logo-email.svg",
  app_subtitle: "by The Studio · The Rahul Gangurde.",
  // What a generation costs. The server is the authority (it is the thing that
  // debits); these are only what we show if /api/config is unreachable.
  generate_credit_cost: 1,
  refine_credit_cost: 1,
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appConfig, setAppConfig] = useState(DEFAULT_CONFIG);

  const checkAuth = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch runtime config once at app boot — runs in parallel with auth check.
  useEffect(() => {
    getAppConfig()
      .then((cfg) => setAppConfig({ ...DEFAULT_CONFIG, ...cfg }))
      .catch(() => setAppConfig(DEFAULT_CONFIG));
  }, []);

  // Reflect the brand name in the browser tab so the portal embedder looks native.
  useEffect(() => {
    if (typeof document !== "undefined" && appConfig?.app_name) {
      document.title = `${appConfig.app_name} — Forge your Irresistible Offer`;
    }
  }, [appConfig?.app_name]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // ignore
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, refresh: checkAuth, logout, appConfig }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
